import React, { MouseEvent, ReactElement, useCallback, useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { useDeviceType } from 'b2b-apps/shop/common/hooks';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useOpenLink, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import {
  EnhancedComponentProps,
  EnhancedComponentType,
  HeaderDataType,
  InjectedProps,
  RowsDataType,
  WrappedComponentType,
} from './';

const withContainer = (
  Component: WrappedComponentType,
): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { variantsMatrix } = props;
    const openLink = useOpenLink();
    const urlCreator = useUrlCreator();
    const variants = useMemo(() => variantsMatrix || [], [variantsMatrix]);
    const areVariants2D = Array.isArray(variants) && variants[0]?.children && variants[0].children.length > 0;

    const handleStockCountClick = useCallback(
      (sku: string) => (e: MouseEvent<HTMLSpanElement>): void => {
        e.preventDefault();
        openLink(
          e,
          urlCreator(ROUTES.product, {
            sku,
          }),
        );
      },
      [openLink, urlCreator],
    );

    const headerData = useMemo(
      (): HeaderDataType => ({
        title: areVariants2D
          ? (variants[0]?.children[0]?.name || '')
          : (variants[0]?.name || ''),
        secondaryTitle: areVariants2D ? variants[0]?.name : undefined,
        options: areVariants2D
          ? variants[0]?.children.map((variantChild) => variantChild.value) || []
          : variants.map((variant) => variant.value),
      }),
      [areVariants2D, variants],
    );

    const rowsData = useMemo(
      (): RowsDataType => {
        if (areVariants2D) {
          return variants.map((variant) => ({
            title: variant.value,
            options: variant.children.map((childRow) => ({
              value: childRow.value,
              sku: childRow.sku,
              stockInfo: childRow.stockInfo,
            })),
          }));
        }

        return [{
          options: variants.map((variant) => ({
            value: variant.value,
            sku: variant.sku,
            stockInfo: variant.stockInfo,
          })),
        }];
      },
      [areVariants2D, variants],
    );

    const injectedProps: InjectedProps = {
      deviceType: useDeviceType(),
      headerData,
      onStockCountClick: handleStockCountClick,
      rowsData,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return React.memo(
    WithContainer,
    (prevProps, nextProps) => isEqual(prevProps, nextProps),
  );
};

export { withContainer };
